/**
 * Download-and-cache layer for the Rules tab's PDFs.
 *
 * Why not bundle: the full catalog is 129 documents / ~236 MB — bundling
 * would take the app past Apple's cellular-download threshold and re-ship
 * a quarter gigabyte on every update. Instead each rulebook downloads the
 * first time it's opened and is cached on-device forever after (instant +
 * offline on every later open).
 *
 * Why CapacitorHttp + Filesystem: the Squarespace CDN sends no CORS
 * headers, so an in-webview fetch() can't read the bytes. CapacitorHttp
 * runs the request at the native layer (no CORS), and Filesystem persists
 * the result under Directory.Data (survives restarts; not purged like
 * Cache). Native-only — the web build opens PDFs in a browser tab, where
 * the browser's own viewer is the right tool.
 */
import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'

// This cache keys by URL alone, so any PDF the site replaces IN PLACE
// needs a version bump here to reach devices that already cached it.
// v2: tokyo-metro print book re-upload (deduped cover spread).
// v3: tokyo-metro again — the v2 file was itself a damaged derivative
//     (hollow type, squeezed geometry); now Jordan's own V2 web export.
// The site team has been asked to rename files on replacement instead.
const CACHE_DIR = 'rules-pdfs-v3'
const OLD_CACHE_DIRS = ['rules-pdfs', 'rules-pdfs-v2']

/** Stable filesystem key for a URL (djb2 hex — collision-safe enough
 *  for a catalog of ~130 fixed URLs). */
function cacheKey(url: string): string {
  let h = 5381
  for (let i = 0; i < url.length; i++) {
    h = ((h << 5) + h + url.charCodeAt(i)) >>> 0
  }
  return `${h.toString(16)}.pdf`
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes
}

export function isNativePdfViewerAvailable(): boolean {
  return Capacitor.isNativePlatform()
}

/**
 * Returns the PDF bytes for `url`, from the on-device cache when present,
 * otherwise downloading once via native HTTP and caching for next time.
 *
 * On the WEB build there is no native layer and no filesystem cache —
 * a plain fetch streams the bytes each time. That works for the new
 * rules API's hosts because they serve with open CORS (the old
 * Squarespace CDN didn't, which is why web used to punt to a browser
 * tab instead of the in-app viewer).
 */
/** Byte-level progress for the viewer's download bar. `total` is null
 *  when the server doesn't announce a Content-Length. */
export type PdfProgress = (loaded: number, total: number | null) => void

let oldCachesCleaned = false
/** Best-effort, once per launch: drop superseded cache directories so
 *  stale PDFs don't sit on disk unreachable. */
async function cleanOldCaches(): Promise<void> {
  if (oldCachesCleaned || !Capacitor.isNativePlatform()) return
  oldCachesCleaned = true
  for (const dir of OLD_CACHE_DIRS) {
    await Filesystem.rmdir({ path: dir, directory: Directory.Data, recursive: true })
      .catch(() => { /* never existed or already gone */ })
  }
}

export async function loadRulesPdf(url: string, onProgress?: PdfProgress): Promise<Uint8Array> {
  void cleanOldCaches()
  if (!Capacitor.isNativePlatform()) {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`)
    const total = Number(res.headers.get('content-length')) || null
    if (!res.body) return new Uint8Array(await res.arrayBuffer())
    // Stream so the bar can move; assemble at the end.
    const reader = res.body.getReader()
    const chunks: Uint8Array[] = []
    let loaded = 0
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      loaded += value.length
      onProgress?.(loaded, total)
    }
    const out = new Uint8Array(loaded)
    let off = 0
    for (const c of chunks) { out.set(c, off); off += c.length }
    return out
  }

  const path = `${CACHE_DIR}/${cacheKey(url)}`

  try {
    const cached = await Filesystem.readFile({ path, directory: Directory.Data })
    if (typeof cached.data === 'string' && cached.data.length > 0) {
      return base64ToBytes(cached.data)
    }
  } catch {
    /* cache miss — fall through to download */
  }

  // Filesystem.downloadFile streams straight to the cache path AND emits
  // byte progress — which CapacitorHttp.get can't do. Still the native
  // layer, so the no-CORS property that motivated CapacitorHttp holds.
  const listener = onProgress
    ? await Filesystem.addListener('progress', e => {
        if (e.url === url) onProgress(e.bytes, e.contentLength || null)
      })
    : null
  try {
    await Filesystem.mkdir({ path: CACHE_DIR, directory: Directory.Data, recursive: true })
      .catch(() => { /* exists */ })
    await Filesystem.downloadFile({
      url, path, directory: Directory.Data, progress: Boolean(onProgress),
    })
  } catch (e) {
    // Don't leave a partial file behind to be mistaken for a cache hit.
    await Filesystem.deleteFile({ path, directory: Directory.Data }).catch(() => {})
    throw e
  } finally {
    listener?.remove()
  }

  const dl = await Filesystem.readFile({ path, directory: Directory.Data })
  if (typeof dl.data !== 'string' || dl.data.length === 0) {
    await Filesystem.deleteFile({ path, directory: Directory.Data }).catch(() => {})
    throw new Error('Download failed (empty file)')
  }
  return base64ToBytes(dl.data)
}

/** Whether a URL is already cached (for the ✓ chip affordance). */
export async function isRulesPdfCached(url: string): Promise<boolean> {
  try {
    const st = await Filesystem.stat({
      path: `${CACHE_DIR}/${cacheKey(url)}`,
      directory: Directory.Data,
    })
    return st.size > 0
  } catch {
    return false
  }
}
